import { Plus, Trash2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { ComposeTextarea } from '@/components/compose-field'
import { InlineFieldInput } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, formatMoney, parseUTCDate } from '@/lib/utils'

import { numberValue } from '../ui/numbers'
import { SurfaceCard } from '../ui/page'
import type { Invoice, InvoicingState, NewPayment, PaymentMethod, RecordedPayment } from './api'
import { openInvoice, PAYMENT_METHODS, paymentDetail } from './payments'

const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const formatDay = (value: string) => shortDate.format(parseUTCDate(value))

function localToday() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

/** Payments recorded by hand (check, wire, ACH) and the form that records one (USA-1043). */
export function PaymentsCard({
  state,
  busy,
  recording,
  removingId,
  onRecord,
  onRemove,
}: {
  state: InvoicingState
  busy: boolean
  recording: boolean
  removingId: string | null
  onRecord: (payment: Omit<NewPayment, 'request_id'>, done: () => void) => void
  onRemove: (paymentId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const owed = numberValue(state.owed ?? 0)
  const target = openInvoice(state)
  const pending = [state.invoice, state.balance_invoice].some((item) => item && !item.finalized_at)
  const canRecord = owed > 0 && !pending
  const payments = state.payments ?? []
  if (!canRecord && payments.length === 0) return null

  return (
    <SurfaceCard
      aria-label="Payments"
      title="Payments"
      actions={
        canRecord && !open ? (
          <Button disabled={busy} onClick={() => setOpen(true)} size="sm" type="button" variant="outline">
            <Plus className="size-4" />
            Record payment
          </Button>
        ) : null
      }
    >
      <dl className="grid grid-cols-2 gap-3 p-4 text-sm tabular-nums">
        <div>
          <dt className="text-xs text-muted-foreground">Paid</dt>
          <dd className="mt-1 text-lg font-semibold">{formatMoney(state.paid)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Owed</dt>
          <dd className={cn('mt-1 text-lg font-semibold', owed > 0 ? undefined : 'text-muted-foreground')}>{formatMoney(state.owed)}</dd>
        </div>
      </dl>
      {open ? (
        <PaymentForm
          busy={busy}
          invoice={target}
          owed={state.owed ?? '0'}
          onCancel={() => setOpen(false)}
          onSubmit={(payment) => onRecord(payment, () => setOpen(false))}
          recording={recording}
        />
      ) : null}
      {payments.length > 0 ? (
        <ul className="divide-y border-t">
          {payments.map((payment) => (
            <PaymentRow
              busy={busy}
              key={payment.id}
              onRemove={() => onRemove(payment.id)}
              payment={payment}
              removing={removingId === payment.id}
            />
          ))}
        </ul>
      ) : null}
    </SurfaceCard>
  )
}

function PaymentRow({
  payment,
  busy,
  removing,
  onRemove,
}: {
  payment: RecordedPayment
  busy: boolean
  removing: boolean
  onRemove: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const removed = Boolean(payment.removed_at)
  // Only a payment recorded without a Stripe invoice can be taken back; Stripe cannot unpay one.
  const removable = !removed && payment.entry_type === 'external_payment'
  // A refund or credit taken off an invoice (USA-378) is money back, not a payment.
  const refund = payment.entry_type === 'refund' || payment.entry_type === 'credit'
  return (
    <li className="grid gap-1 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className={cn('font-medium tabular-nums', removed && 'text-muted-foreground line-through')}>{refund ? `−${formatMoney(payment.amount)}` : formatMoney(payment.amount)}</span>
        <span className="text-xs text-muted-foreground">{formatDay(payment.occurred_at)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 text-xs text-muted-foreground">
          {refund ? `${payment.entry_type === 'refund' ? 'Refunded' : 'Taken off an invoice'} · ` : ''}
          {paymentDetail(payment)}
          {payment.invoice_id && !refund ? ' · Invoice marked paid' : ''}
        </p>
        {removable && !confirming ? (
          <Button
            aria-label={`Remove the ${formatMoney(payment.amount)} payment`}
            className="-my-1 h-7 shrink-0 px-2 text-muted-foreground"
            disabled={busy}
            onClick={() => setConfirming(true)}
            size="sm"
            type="button"
            variant="ghost"
          >
            <Trash2 className="size-4" />
            Remove
          </Button>
        ) : null}
      </div>
      {payment.note ? <p className="whitespace-pre-line text-xs text-muted-foreground">{payment.note}</p> : null}
      {removed ? (
        <p className="text-xs text-muted-foreground">
          Removed{payment.removed_by ? ` by ${payment.removed_by.name}` : ''} {payment.removed_at ? formatDay(payment.removed_at) : ''}
        </p>
      ) : null}
      {removable && confirming ? (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button disabled={removing} onClick={() => setConfirming(false)} size="sm" type="button" variant="ghost">
            Keep
          </Button>
          <LoadingButton
            disabled={busy}
            loading={removing}
            loadingText="Removing..."
            onClick={onRemove}
            size="sm"
            type="button"
            variant="destructive"
          >
            Remove {formatMoney(payment.amount)}
          </LoadingButton>
        </div>
      ) : null}
    </li>
  )
}

function PaymentForm({
  invoice,
  owed,
  busy,
  recording,
  onSubmit,
  onCancel,
}: {
  invoice: Invoice | null
  owed: string
  busy: boolean
  recording: boolean
  onSubmit: (payment: Omit<NewPayment, 'request_id'>) => void
  onCancel: () => void
}) {
  const [method, setMethod] = useState<PaymentMethod | null>(null)
  const [amount, setAmount] = useState(String(numberValue(invoice ? invoice.amount_remaining : owed).toFixed(2)))
  const [paidOn, setPaidOn] = useState(localToday)
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const value = Number(amount)
  const valid = method !== null && Number.isFinite(value) && value > 0 && value <= numberValue(owed) && Boolean(paidOn)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid || busy || method === null) return
    onSubmit({ amount: value.toFixed(2), paid_on: paidOn, method, reference: reference.trim() || null, note: note.trim() || null })
  }

  return (
    <form className="grid gap-3 border-t bg-muted/30 p-4" onSubmit={submit}>
      <ReviewChoiceGroup
        className="grid grid-cols-3 sm:grid-cols-6"
        label="Payment method"
        onChange={setMethod}
        optionClassName="justify-center"
        options={PAYMENT_METHODS}
        value={method}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1.5 text-sm font-medium">
          Amount
          <InlineFieldInput
            disabled={Boolean(invoice)}
            inputMode="decimal"
            max={numberValue(owed)}
            min="0.01"
            onChange={(event) => setAmount(event.target.value)}
            required
            step="0.01"
            type="number"
            value={amount}
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium">
          Date paid
          <Input className="h-8" max={localToday()} onChange={(event) => setPaidOn(event.target.value)} required type="date" value={paidOn} />
        </label>
      </div>
      <label className="grid gap-1.5 text-sm font-medium">
        Reference <span className="sr-only">(optional)</span>
        <InlineFieldInput maxLength={120} onChange={(event) => setReference(event.target.value)} placeholder="Check or wire number" value={reference} />
      </label>
      <label className="grid gap-1.5 text-sm font-medium">
        Note <span className="sr-only">(optional)</span>
        <ComposeTextarea className="min-h-10" maxLength={2000} onChange={(event) => setNote(event.target.value)} value={note} />
      </label>
      <div className="flex flex-wrap justify-end gap-2">
        <Button disabled={recording} onClick={onCancel} size="sm" type="button" variant="ghost">
          Cancel
        </Button>
        <LoadingButton disabled={!valid || busy} loading={recording} loadingText="Recording..." size="sm" type="submit">
          {invoice ? `Mark ${invoice.sequence === 2 ? 'balance invoice' : 'invoice'} paid` : 'Record payment'}
        </LoadingButton>
      </div>
    </form>
  )
}
